<template>
  <div>
    <v-layout row wrap>
      <v-flex :class="{ xs6: !small }">
        <pie-chart
          :chart-data="data"
          :options="options"
          :width="size"
          :height="size"
        />
      </v-flex>
      <v-flex xs6 v-if="!small && $auth.hasRole('GROUP_ADMIN')">
        <v-card>
          <v-card-title>{{ $t('logicStorageReport.displayFilters') }}</v-card-title>
          <v-divider />
          <v-card-text>
            <loadable :loading="!groups">
              <v-text-field
                v-model="filter"
                :label="$t('search')"
                prepend-icon="search"
                hide-details
              />

              <table class="datatable table table-crud elevation-1">
                <thead>
                  <tr>
                    <th class="show-check">{{ $t('headers.show') }}</th>
                    <th>{{ $t('headers.name') }}</th>
                    <th>{{ $t('headers.totalSpace') }}</th>
                    <th>{{ $t('headers.takenSpace') }}</th>
                    <th>{{ $t('headers.disabledSpace') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody v-for="group in filtered" :key="group.id">
                  <tr>
                    <td><v-checkbox class="ma-0 pa-0" v-model="group.checked" /></td>
                    <td @click="group.showChildren = !group.showChildren">
                      <v-icon>{{ group.showChildren ? 'expand_less' : 'expand_more' }}</v-icon>
                      <span>{{ group.name }}</span>
                    </td>
                    <td>{{ $format.fileSize(group.total) }}</td>
                    <td>{{ $format.fileSize(group.used) }} <div class='percentage'>{{ $format.ratio(group.used, group.total) }}</div></td>
                    <td>{{ $format.fileSize(group.inactive) }} <div class='percentage'>{{ $format.ratio(group.inactive, group.total) }}</div></td>
                    <td>
                      <v-btn
                        icon
                        class="pa-0 ma-0"
                        @click="showOnly(group, null)"
                        v-tooltip="{ content: $t('logicStorageReport.showOnly') }"
                      >
                        <v-icon>arrow_forward</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-show="group.showChildren">
                    <td colspan="200">
                      <table class="datatable table table-crud ma-3 subtable elevation-1">
                        <thead>
                          <tr>
                            <th class="show-check">{{ $t('headers.show') }}</th>
                            <th>{{ $t('headers.name') }}</th>
                            <th>{{ $t('headers.quota') }}</th>
                            <th>{{ $t('headers.takenSpace') }}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="company in group.filtered" :key="company.id">
                            <td><v-checkbox class="ma-0 pa-0" v-model="company.checked" /></td>
                            <td>{{ company.name }}</td>
                            <td>{{ $format.fileSize(company.quota || 0) }}</td>
                            <td>{{ $format.fileSize(company.recordingsSize || 0 )}} <div class='percentage'>{{ $format.ratio(company.recordingsSize, company.quota) }}</div></td>
                            <td>
                              <v-btn
                                icon
                                class="pa-0 ma-0"
                                @click="showOnly(group, company)"
                                v-tooltip="{ content: $t('logicStorageReport.showOnly') }"
                              >
                                <v-icon>arrow_forward</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </loadable>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex>
        <v-card>
          <v-card-title>{{ $t('targetsReport.title') }}</v-card-title>
          <v-divider />
          <v-card-text>
            <targets-report />
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import PieChart from '@/components/PieChart'
import Company from '@/models/Company'
import CompanyGroup from '@/models/CompanyGroup'
import TargetsReport from './LogicStorageReport/TargetsReport'

export default {
  props: {
    size: {
      type: Number,
      default: 300
    },
    small: Boolean
  },
  data () {
    let self = this

    return {
      groups: null,
      filter: null,
      filtered: null,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            label: (tooltipItems, data) => {
              let sum = data.datasets[0].data.reduce((a, b) => a + b, 0)
              return Math.round((data.datasets[0].data[tooltipItems.index] / sum) * 10000) / 100 + ' %'
            },
            title: (tooltipItems, data) => {
              return data.labels[tooltipItems[0].index] + ': ' + self.$format.fileSize(data.datasets[0].data[tooltipItems[0].index])
            }
          },
          titleFontColor: '#fff',
          bodyFontColor: '#fff',
          footerFontColor: '#fff'
        }
      }
    }
  },
  created () {
    if (this.$auth.hasRole('GROUP_ADMIN')) {
      CompanyGroup.getList({
        size: 99999,
        include: ['companies']
      }).then(result => {
        // We need to do some postprocessing
        result.items.forEach(item => {
          // This needs to be done to allow watching for changes
          item.checked = true
          item.showChildren = false

          // This makes displaying stuff easier
          item.total = item.companies.reduce((sum, item) => sum + item.quota, 0)
          item.used = item.companies.reduce((sum, item) => sum + (item.enabled ? item.recordingsSize : 0), 0)
          item.inactive = item.companies.reduce((sum, item) => sum + (item.enabled ? 0 : item.recordingsSize), 0)

          // This needs to be done to allow watching for changes
          item.companies.forEach(company => {
            company.checked = true
          })
        })

        // Now save groups (triggers computed props)
        this.groups = result.items
      })
    } else {
      Company.getList({
        size: 999999
      }).then(result => {
        this.groups = [
          {
            checked: true,
            companies: result.items.map(item => {
              item.checked = true
              return item
            })
          }
        ]
      })
    }
  },
  watch: {
    filter () {
      this.filtered = this.getFiltered()
    },
    groups () {
      this.filtered = this.getFiltered()
    }
  },
  computed: {
    data () {
      // No data? Nothing to display
      if (!this.filtered || this.filtered.length <= 0) {
        return {
          datasets: [],
          labels: []
        }
      }

      // Only use checked items
      let checked = this.filtered
        .map(group => { return { ...group } })
        .filter(group => {
          group.filtered = group.filtered.filter(company => company.checked)
          return group.checked
        })

      // Compute summary fields
      let used = checked.reduce((sum, group) => sum + group.filtered.reduce((sum, item) => sum + (item.enabled ? item.recordingsSize : 0), 0), 0)
      let total = checked.reduce((sum, group) => sum + group.filtered.reduce((sum, item) => sum + item.quota, 0), 0)
      let inactive = checked.reduce((sum, group) => sum + group.filtered.reduce((sum, item) => sum + (!item.enabled ? item.recordingsSize : 0), 0), 0)

      // Different roles see different things
      if (this.$auth.hasRole('COMPANY_ADMIN')) {
        return {
          datasets: [
            {
              backgroundColor: [
                '#E46651',
                '#41B883'
              ],
              data: [used + inactive, Math.max(0, total - (used + inactive))]
            }
          ],
          labels: [ this.$t('space.used'), this.$t('space.free') ]
        }
      }

      return {
        datasets: [
          {
            backgroundColor: [
              '#E46651',
              '#EEEEEE',
              '#41B883'
            ],
            data: [used, inactive, Math.max(0, total - (used + inactive))]
          }
        ],
        labels: [ this.$t('space.used'), this.$t('space.inactive'), this.$t('space.free') ]
      }
    }
  },
  methods: {
    /**
     * Computes filtered array, must be done by hand because reference issues
     * would destroy the checkboxes.
     * @return {object[]}
     */
    getFiltered () {
      return this.groups
        .filter(group => {
          group.filtered = group.companies.filter(company => {
            return !this.filter || this.filterMatch(company.name, this.filter)
          })
          if (this.filter) {
            group.showChildren = true
          }
          return group.filtered.length > 0
        })
    },

    /**
     * Returns if input string matches filter
     * @param {string} input
     * @param {string} filter
     * @return {boolean}
     */
    filterMatch (input, filter) {
      return input.toLowerCase().indexOf(filter.toLowerCase()) >= 0
    },

    /**
     * Updates selected items to only show specified items
     * @param {object} showGroup group to be shown
     * @param {object?} showCompany company to be shown, if null whole group will be shown
     */
    showOnly (showGroup, showCompany) {
      if (!showCompany) {
        this.groups.forEach(group => {
          group.checked = group.id === showGroup.id
          group.companies.forEach(company => {
            company.checked = group.id === showGroup.id
          })
        })
      } else {
        this.groups.forEach(group => {
          group.checked = group.id === showGroup.id
          group.companies.forEach(company => {
            company.checked = company.id === showCompany.id
          })
        })
      }
    }
  },
  components: {
    PieChart,
    TargetsReport
  }
}
</script>

<style>
.show-check {
  width: 10px;
}

.subtable thead tr,
.subtable thead tr th {
  height: auto !important;
}

.percentage {
  font-size: 85%;
  color: #999;
}
</style>



// WEBPACK FOOTER //
// LogicStorageReport.vue?58700770